import type { ZodType } from 'zod';
import i18next from 'i18next';
import { apiError, type ErrorParams } from '@overseer/contracts';
import { apiUrl } from '../config';
import { accessToken } from '../auth/session';
import { errorText } from './error-text';

/**
 * The one way this application talks to the API.
 *
 * Explicitly, in one function, which is the point: every call site attaches
 * its own `Authorization` header, so the coupling to the session is visible
 * rather than installed globally behind a magic string.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues?: { path: string; message: string }[],
    /**
     * The API's reason as a key of `ERRORS` in the contracts, when it sent one;
     * `message` is already its text in the person's language.
     *
     * After `issues`, so every `new ApiError(message, status, issues)` call keeps
     * its arguments where they were.
     */
    readonly code?: string,
    readonly params?: ErrorParams,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The session is gone or was never valid; the caller should re-authenticate. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Authenticated, but not allowed. A capability check should have prevented this. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * The write landed in a closed month.
   *
   * On the code rather than on the 409: a conflict is also how an overlapping
   * agreement and a still-referenced device are reported, and telling those apart
   * by reading the message is the sniffing `code` exists to remove.
   */
  get isMonthClosed(): boolean {
    return this.code === 'payroll.monthClosed';
  }

  /**
   * The write landed on a row somebody has already approved.
   *
   * Separate from `isMonthClosed` rather than folded into one "is it frozen?"
   * getter, because the two have different ways out and the UI offers them to
   * different people. A closed month is reopened by an administrator with a stated
   * reason; an approved row is unticked by whoever is looking at it. One getter
   * would have to pick one sentence, and it would be wrong half the time.
   */
  get isEntryLocked(): boolean {
    return this.code === 'payroll.entryLocked';
  }
}

/**
 * The request never reached the API.
 *
 * `fetch` rejects with a bare `TypeError: Failed to fetch` for an offline
 * machine, a DNS failure, a refused connection and a CORS rejection alike — no
 * status, no body, and a message in the browser's language rather than the app's.
 * Rendering that string put untranslated Chrome internals in front of a Polish
 * user and said nothing about what to do.
 *
 * Distinguishing it matters beyond the wording: there is no status to branch on,
 * so without this class every "is this a 401?" check reads a network failure as a
 * server response that simply is not a 401.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    // `cause` through the options bag rather than as a property of our own: it is
    // already a member of Error, and shadowing it needs an `override` that says
    // nothing.
    super(i18next.t('common.networkError'), { cause });
    this.name = 'NetworkError';
  }
}

/** A response the schema rejected. Distinct from a transport error on purpose. */
export class ContractError extends Error {
  constructor(
    readonly path: string,
    readonly issues: string[],
  ) {
    super(`Response from ${path} did not match its contract:\n${issues.join('\n')}`);
    this.name = 'ContractError';
  }
}

interface RequestOptions {
  // PUT for a body that is a complete replacement — a person's whole access,
  // `/v1/admin/users/:id`. PATCH changes some fields and leaves the rest.
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  searchParams?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

const toQueryString = (params: RequestOptions['searchParams']): string => {
  if (!params) {
    return '';
  }

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  }

  const query = search.toString();

  return query ? `?${query}` : '';
};

/**
 * Performs a request and parses the response against its contract schema.
 *
 * The schema is not optional. Validating on the way in is what makes
 * `packages/contracts` a guarantee rather than a convention — the API validates
 * on the way out through `ZodResponseInterceptor`, and this is the other half.
 * A field the server stops sending fails here, naming the field, instead of
 * surfacing as `undefined` three components away.
 */
export const request = async <T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> => {
  const token = await accessToken();
  const url = apiUrl(path) + toQueryString(options.searchParams);

  let response: Response;

  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      signal: options.signal,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        // No token means no header, so the API answers 401 rather than the client
        // guessing at the session state.
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });
  } catch (caught) {
    // An aborted request is not a failure — TanStack Query cancels in-flight
    // fetches on unmount and on `refresh()`, and turning those into an error
    // banner would make navigating away look like the network died.
    if (caught instanceof DOMException && caught.name === 'AbortError') throw caught;

    throw new NetworkError(caught);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return schema.parse(undefined);
  }

  const payload: unknown = await response.json();
  const result = schema.safeParse(payload);

  if (!result.success) {
    throw new ContractError(
      path,
      result.error.issues.map(
        (issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`,
      ),
    );
  }

  return result.data;
};

/**
 * Fetches a file and hands the browser a save dialog.
 *
 * A sibling of `request` rather than a mode of it: that one's contract is "parse
 * the body against a schema", and a spreadsheet has no schema to parse against.
 * Bending it to return a `Blob` would make the schema argument optional, and an
 * optional schema is the thing `request` exists to prevent.
 *
 * The reason this cannot be an anchor tag with an `href`: the API takes a bearer
 * token, and a browser navigation sends no `Authorization` header. So the file
 * is fetched like any other request, turned into an object URL, and clicked.
 *
 * The server names the file in `Content-Disposition`; `fallbackName` is used
 * when that header is unreadable. Cross-origin, it is only readable because
 * `main.ts` lists it in `exposedHeaders` — without that the name silently
 * becomes the fallback rather than failing.
 */
export const download = async (
  path: string,
  fallbackName: string,
  options: Pick<RequestOptions, 'searchParams' | 'signal'> = {},
): Promise<void> => {
  const token = await accessToken();
  const url = apiUrl(path) + toQueryString(options.searchParams);

  let response: Response;

  try {
    response = await fetch(url, {
      signal: options.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === 'AbortError') throw caught;

    throw new NetworkError(caught);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = filenameFrom(response.headers.get('Content-Disposition')) ?? fallbackName;
  document.body.append(link);
  link.click();
  link.remove();

  // Revoking immediately can cancel the download in Safari, which reads the blob
  // after the click returns. A tick is enough and the URL is a leak until it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

/**
 * The file name out of a Content-Disposition header, if it has one: the
 * UTF-8 `filename*` (RFC 6266) for a name with Polish letters, else `filename`.
 */
export const filenameFrom = (header: string | null): string | undefined => {
  if (!header) {
    return undefined;
  }

  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed escape: the plain name below.
    }
  }

  const match = /filename="?([^";]+)"?/i.exec(header);

  return match?.[1];
};

/**
 * A 403 is an ordinary answer, not a lost session: somebody narrowed to one
 * outlet follows a link to another, or a viewer reaches a write. It stays on
 * the screen as an error that says so in the app's language, and never signs
 * anybody out. Only a 401 means the session is gone. A coded 403 already says
 * why in the app's language; an uncoded one gets the server's reason after a
 * translated sentence.
 */
const toApiError = async (response: Response): Promise<ApiError> => {
  const error = await parseApiError(response);

  if (!error.isForbidden || error.code) return error;

  const reason = error.message === `${response.status} ${response.statusText}` ? '' : error.message;

  return new ApiError(
    reason ? i18next.t('common.forbiddenBecause', { reason }) : i18next.t('common.forbidden'),
    403,
    error.issues,
    error.code,
  );
};

const parseApiError = async (response: Response): Promise<ApiError> => {
  const fallback = `${response.status} ${response.statusText}`;

  try {
    const body: unknown = await response.json();
    const parsed = apiError.safeParse(body);

    if (parsed.success) {
      const { message, code, params, issues } = parsed.data;

      return new ApiError(
        errorText(code, params, message),
        response.status,
        issues?.map((issue) => ({ path: issue.path, message: errorText(issue.code, issue.params, issue.message) })),
        code,
        params,
      );
    }

    // Nest's default error body, or anything else. Keep the status, which is the
    // part the caller branches on.
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : fallback;

    return new ApiError(message, response.status);
  } catch {
    return new ApiError(fallback, response.status);
  }
};

/**
 * Posts a file and parses the answer against its contract.
 *
 * A third sibling of `request` rather than a `body: FormData` branch inside it.
 * `request` does two things to a body that are exactly wrong for an upload: it
 * `JSON.stringify`s it — `FormData` stringifies to `"{}"`, so the file silently
 * never leaves the browser — and it sets `Content-Type: application/json`.
 * Setting that header at all is the subtler failure: a multipart body needs the
 * `boundary=…` parameter, which only `fetch` can supply, and it does so *only*
 * when no `Content-Type` is set by hand. Naming the type here would produce a
 * request the server cannot split back into parts.
 *
 * Branching inside `request` would mean a body-shaped condition around both of
 * those, on the one function whose simplicity is the point. This keeps the
 * schema-on-the-way-in guarantee and drops the two lines that do not apply.
 */
export const upload = async <T>(
  path: string,
  schema: ZodType<T>,
  /** The form to send. The caller names the field, because the API does. */
  form: FormData,
  options: Pick<RequestOptions, 'signal'> = {},
): Promise<T> => {
  const token = await accessToken();
  const url = apiUrl(path);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'POST',
      signal: options.signal,
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === 'AbortError') throw caught;

    throw new NetworkError(caught);
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  const payload: unknown = await response.json();
  const result = schema.safeParse(payload);

  if (!result.success) {
    throw new ContractError(
      path,
      result.error.issues.map(
        (issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`,
      ),
    );
  }

  return result.data;
};
